import { MS_DOCUMENT_TYPES_MAP, MS_MARKETS_MAP } from "@repo/shared";
import type { LocalizedString, ProductDocumentType } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState, useTransition } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TranslationButton } from "@/components/ui/translation-button";
import { checkDeleteDocumentType } from "@/lib/api/product-documents";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useDeleteDocumentType, useUpdateDocumentType } from "@/lib/hooks/use-product-documents";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { ProductDocumentTypeDialog } from "./product-document-type-dialog";
import { T } from "./product-document-types-table.i18n";

type ProductDocumentTypesTableProps = {
  portfolioCollectionId: string;
  documentTypes: ProductDocumentType[];
};

export function ProductDocumentTypesTable({
  portfolioCollectionId,
  documentTypes,
}: ProductDocumentTypesTableProps) {
  const { tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  // Helper to get localized value
  const getName = (i18n: LocalizedString) => localize(i18n);

  const [isPending, startTransition] = useTransition();
  const updateDocumentTypeMutation = useUpdateDocumentType();
  const deleteDocumentTypeMutation = useDeleteDocumentType();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<ProductDocumentType | null>(null);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<string | null>(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  // QL-168 — a document type the server would refuse to delete shows why on
  // the Delete click, before the confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (typeId) => checkDeleteDocumentType(portfolioCollectionId, typeId),
    onAllowed: (typeId) => {
      setTypeToDelete(typeId);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDelete, "destructive"),
  });

  const handleDelete = async () => {
    if (!typeToDelete) return;

    startTransition(async () => {
      try {
        await deleteDocumentTypeMutation.mutateAsync({
          collectionId: portfolioCollectionId,
          typeId: typeToDelete,
        });
      } catch (err) {
        showAlert(t.error, err instanceof Error ? err.message : t.failedToDelete, "destructive");
      }
      setTypeToDelete(null);
    });
  };

  const handleToggleActive = async (type: ProductDocumentType) => {
    startTransition(async () => {
      try {
        await updateDocumentTypeMutation.mutateAsync({
          collectionId: portfolioCollectionId,
          typeId: type.id,
          data: {
            nameI18n: type.nameI18n,
            msDocumentTypeId: type.msDocumentTypeId || undefined,
            msMarket: type.msMarket || undefined,
            isActive: !type.isActive,
          },
        });
      } catch (err) {
        showAlert(t.error, err instanceof Error ? err.message : t.failedToUpdate, "destructive");
      }
    });
  };

  const handleTranslationSave = async (typeId: string, nameI18n: LocalizedString) => {
    const type = documentTypes.find((dt) => dt.id === typeId);
    if (!type) return;

    try {
      await updateDocumentTypeMutation.mutateAsync({
        collectionId: portfolioCollectionId,
        typeId,
        data: {
          nameI18n,
          msDocumentTypeId: type.msDocumentTypeId || undefined,
          msMarket: type.msMarket || undefined,
          isActive: type.isActive,
        },
      });
    } catch (err) {
      showAlert(t.error, err instanceof Error ? err.message : t.failedToUpdate, "destructive");
    }
  };

  const handleEdit = (type: ProductDocumentType) => {
    setEditingType(type);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingType(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingType(null);
  };

  const formatMorningstarMapping = (type: ProductDocumentType): string => {
    if (!type.msDocumentTypeId && !type.msMarket) {
      return t.notConfigured;
    }

    const parts: string[] = [];
    if (type.msDocumentTypeId) {
      const label = MS_DOCUMENT_TYPES_MAP[type.msDocumentTypeId] || type.msDocumentTypeId;
      parts.push(`${t.type}: ${label}`);
    }
    if (type.msMarket) {
      const label = MS_MARKETS_MAP[type.msMarket] || type.msMarket;
      parts.push(`${t.market}: ${label}`);
    }

    return parts.join(", ");
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          <Button onClick={handleCreate} disabled={isPending}>
            {t.addDocumentType}
          </Button>
        </div>

        {documentTypes.length === 0 ? (
          <EmptyState>{t.noDocumentTypes}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.morningstarMapping}</TableHead>
                <TableHead>{t.active}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documentTypes.map((type) => (
                <TableRow key={type.id}>
                  <TableCell className="font-medium">{getName(type.nameI18n)}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatMorningstarMapping(type)}
                  </TableCell>
                  <TableCell>
                    <Switch
                      aria-label={`${t.active}: ${getName(type.nameI18n)}`}
                      checked={type.isActive}
                      onCheckedChange={() => handleToggleActive(type)}
                      disabled={isPending}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <Button
                        size="icon"
                        tooltip={t.edit}
                        variant="ghost"
                        onClick={() => handleEdit(type)}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <TranslationButton
                        value={type.nameI18n}
                        onSave={(nameI18n) => handleTranslationSave(type.id, nameI18n)}
                        supportedLanguages={tenantSupportedLanguages}
                        disabled={isPending}
                      />
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        loading={checkingId === type.id}
                        onClick={() => requestDelete(type.id)}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <ProductDocumentTypeDialog
        open={dialogOpen}
        portfolioCollectionId={portfolioCollectionId}
        documentType={editingType}
        onClose={handleDialogClose}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteDocumentType}
        description={t.deleteConfirmation}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
